import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Button,
  Divider,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { adminApi } from '../../api/client';
import { useLanguage } from '../../i18n';

interface LogEntry {
  id: number;
  user_id: number | null;
  user_email: string | null;
  action: string;
  ip_address: string;
  details: string | null;
  timestamp: string;
}

interface BlockedIP {
  id: number;
  ip_address: string;
  blocked_at: string;
  blocked_until: string;
  reason: string;
  is_permanent: boolean;
}

const AdminLogs: React.FC = () => {
  const { t } = useLanguage();
  // Logs state
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalLogs, setTotalLogs] = useState(0);

  // Filters
  const [actionFilter, setActionFilter] = useState('');

  // Blocked IPs state
  const [blockedIPs, setBlockedIPs] = useState<BlockedIP[]>([]);
  const [loadingIPs, setLoadingIPs] = useState(true);

  useEffect(() => {
    fetchLogs();
    fetchBlockedIPs();
  }, [page, rowsPerPage, actionFilter]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params: any = {
        page: page + 1,
        page_size: rowsPerPage,
      };
      if (actionFilter) params.action = actionFilter;

      const response = await adminApi.getLogs(params);
      setLogs(response.data.items);
      setTotalLogs(response.data.total);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('admin.failedLoadLogs'));
    } finally {
      setLoading(false);
    }
  };

  const fetchBlockedIPs = async () => {
    setLoadingIPs(true);
    try {
      const response = await adminApi.getBlockedIPs();
      setBlockedIPs(response.data);
    } catch (err) {
      // Silently fail for blocked IPs
    } finally {
      setLoadingIPs(false);
    }
  };

  const handleUnblockIP = async (ip: string) => {
    try {
      await adminApi.unblockIP(ip);
      fetchBlockedIPs();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to unblock IP');
    }
  };

  const getActionColor = (action: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (action) {
      case 'login':
        return 'success';
      case 'logout':
        return 'default';
      case 'password_change':
        return 'warning';
      case 'create_user':
      case 'update_user':
        return 'info';
      case 'delete_user':
      case 'revoke_access':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const actionOptions = [
    { value: '', label: t('admin.allActions') },
    { value: 'login', label: 'Login' },
    { value: 'logout', label: 'Logout' },
    { value: 'password_change', label: 'Password Change' },
    { value: 'fetch_potholes', label: 'Fetch Potholes' },
    { value: 'create_user', label: 'Create User' },
    { value: 'update_user', label: 'Update User' },
    { value: 'delete_user', label: 'Delete User' },
    { value: 'revoke_access', label: 'Revoke Access' },
    { value: 'unblock_ip', label: 'Unblock IP' },
  ];

  return (
    <Box>
      {/* Blocked IPs Section */}
      {blockedIPs.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" color="error" gutterBottom>
              {t('admin.blockedIPs')} ({blockedIPs.length})
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('admin.ipAddress')}</TableCell>
                    <TableCell>{t('admin.blockedAt')}</TableCell>
                    <TableCell>{t('admin.blockedUntil')}</TableCell>
                    <TableCell>{t('admin.reason')}</TableCell>
                    <TableCell>{t('admin.action')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {blockedIPs.map((blocked) => (
                    <TableRow key={blocked.id}>
                      <TableCell>
                        <code>{blocked.ip_address}</code>
                      </TableCell>
                      <TableCell>
                        {new Date(blocked.blocked_at).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        {blocked.is_permanent
                          ? t('admin.permanent')
                          : new Date(blocked.blocked_until).toLocaleString()}
                      </TableCell>
                      <TableCell>{blocked.reason}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleUnblockIP(blocked.ip_address)}
                        >
                          {t('admin.unblock')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Card>
      )}

      {/* Access Logs Section */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h5" fontWeight={600}>
          {t('admin.accessLogs')}
        </Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={() => {
            fetchLogs();
            fetchBlockedIPs();
          }}
        >
          {t('admin.refresh')}
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('admin.actionType')}</InputLabel>
                <Select
                  value={actionFilter}
                  onChange={(e) => {
                    setActionFilter(e.target.value);
                    setPage(0);
                  }}
                  label={t('admin.actionType')}
                >
                  {actionOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('admin.timestamp')}</TableCell>
                <TableCell>{t('admin.user')}</TableCell>
                <TableCell>{t('admin.action')}</TableCell>
                <TableCell>{t('admin.ipAddress')}</TableCell>
                <TableCell>{t('admin.details')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    {t('admin.noLogs')}
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {new Date(log.timestamp).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {log.user_email || (
                        <Typography variant="body2" color="text.secondary">
                          {t('admin.unknown')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={log.action}
                        size="small"
                        color={getActionColor(log.action)}
                      />
                    </TableCell>
                    <TableCell>
                      <code>{log.ip_address}</code>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 300,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={log.details || ''}
                      >
                        {log.details || '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalLogs}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Card>
    </Box>
  );
};

export default AdminLogs;
